import React, { useState, useEffect } from 'react';
import { BarChart3, PieChart, TrendingUp, TrendingDown, DollarSign, FileText, Clock, CheckCircle, AlertTriangle, Download, Printer, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { baseURL } from '@/config/APIConfig';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const API_URL = baseURL.AiBaseURL;

const Reports = () => {
    const { t } = useLanguage();
    const [summary, setSummary] = useState(null);
    const [agingAR, setAgingAR] = useState(null);
    const [agingAP, setAgingAP] = useState(null);
    const [trialBalance, setTrialBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('summary');

    useEffect(() => {
        fetchSummary();
        fetchAgingReports();
        fetchTrialBalance();
    }, []);

    const fetchSummary = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/summary`);
            const data = await response.json();
            setSummary(data);
        } catch (error) {
            console.error('Error fetching summary:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAgingReports = async () => {
        try {
            const [arRes, apRes] = await Promise.all([
                fetch(`${API_URL}/api/accounting/reports/aging?report_type=ar`),
                fetch(`${API_URL}/api/accounting/reports/aging?report_type=ap`)
            ]);
            const arData = await arRes.json();
            const apData = await apRes.json();
            setAgingAR(arData);
            setAgingAP(apData);
        } catch (error) {
            console.error('Error fetching aging reports:', error);
        }
    };

    const fetchTrialBalance = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/reports/trial-balance`);
            const data = await response.json();
            setTrialBalance(data);
        } catch (error) {
            console.error('Error fetching trial balance:', error);
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

    const refreshAll = () => {
        setLoading(true);
        fetchSummary();
        fetchAgingReports();
        fetchTrialBalance();
    };

    const TABS = [
        { id: 'summary', label: t('summary'), icon: PieChart },
        { id: 'aging-ar', label: t('arAging'), icon: TrendingUp },
        { id: 'aging-ap', label: t('apAging'), icon: TrendingDown },
        { id: 'trial-balance', label: t('trialBalance'), icon: BarChart3 },
    ];

    return (
        <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                            <BarChart3 className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{t('financialReports')}</h1>
                            <p className="text-slate-300">{t('analyticsReporting')}</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={refreshAll} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="refresh-reports-btn">
                            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> {t('refresh')}
                        </Button>
                        <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="export-reports-btn">
                            <Download className="w-4 h-4 mr-2" /> {t('export')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
                {TABS.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                                activeTab === tab.id 
                                    ? 'bg-blue-600 text-white shadow-lg' 
                                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Summary Tab */}
            {activeTab === 'summary' && summary && (
                <div className="space-y-6">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white">
                            <div className="flex items-center justify-between mb-3">
                                <TrendingUp className="w-6 h-6 opacity-80" />
                            </div>
                            <p className="text-2xl font-bold">{formatCurrency(summary.total_revenue)}</p>
                            <p className="text-green-100 text-sm">{t('totalRevenue')}</p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-xl p-5 text-white">
                            <div className="flex items-center justify-between mb-3">
                                <TrendingDown className="w-6 h-6 opacity-80" />
                            </div>
                            <p className="text-2xl font-bold">{formatCurrency(summary.total_expenses)}</p>
                            <p className="text-red-100 text-sm">{t('totalExpenses')}</p>
                        </div>
                        
                        <div className={`bg-gradient-to-br ${summary.net_profit >= 0 ? 'from-blue-500 to-indigo-600' : 'from-red-500 to-rose-600'} rounded-xl p-5 text-white`}>
                            <div className="flex items-center justify-between mb-3">
                                <DollarSign className="w-6 h-6 opacity-80" />
                            </div>
                            <p className="text-2xl font-bold">{formatCurrency(summary.net_profit)}</p>
                            <p className={summary.net_profit >= 0 ? 'text-blue-100' : 'text-red-100'} style={{fontSize: '0.875rem'}}>{t('netProfit')}</p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white">
                            <div className="flex items-center justify-between mb-3">
                                <Clock className="w-6 h-6 opacity-80" />
                            </div>
                            <p className="text-2xl font-bold">{formatCurrency(summary.pending_receivables)}</p>
                            <p className="text-amber-100 text-sm">{t('pendingAR')}</p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl p-5 text-white">
                            <div className="flex items-center justify-between mb-3">
                                <AlertTriangle className="w-6 h-6 opacity-80" />
                            </div>
                            <p className="text-2xl font-bold">{formatCurrency(summary.pending_payables)}</p>
                            <p className="text-purple-100 text-sm">{t('pendingAP')}</p>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{summary.invoice_count}</p>
                                    <p className="text-sm text-slate-500">{t('totalInvoices')}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{summary.paid_invoice_count}</p>
                                    <p className="text-sm text-slate-500">{t('paidInvoices')}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{summary.bill_count}</p>
                                    <p className="text-sm text-slate-500">{t('totalBills')}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{summary.paid_bill_count}</p>
                                    <p className="text-sm text-slate-500">{t('paidBills')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AR Aging Tab */}
            {activeTab === 'aging-ar' && agingAR && (
                <div className="space-y-6">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-bold mb-4">{t('arAging')}</h2>
                        <p className="text-sm text-slate-500 mb-6">As of {agingAR.as_of_date}</p>
                        
                        {/* Aging Buckets Summary */}
                        <div className="grid grid-cols-6 gap-4 mb-8">
                            {[
                                { key: 'current', label: 'Current', color: 'bg-green-500' },
                                { key: '1_30', label: '1-30 Days', color: 'bg-blue-500' },
                                { key: '31_60', label: '31-60 Days', color: 'bg-amber-500' },
                                { key: '61_90', label: '61-90 Days', color: 'bg-orange-500' },
                                { key: 'over_90', label: '90+ Days', color: 'bg-red-500' },
                                { key: 'total', label: 'Total', color: 'bg-slate-700' },
                            ].map(bucket => (
                                <div key={bucket.key} className={`${bucket.color} rounded-lg p-4 text-white`}>
                                    <p className="text-xs opacity-80">{bucket.label}</p>
                                    <p className="text-xl font-bold">{formatCurrency(agingAR.totals[bucket.key])}</p>
                                </div>
                            ))}
                        </div>

                        {/* Aging Details */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="px-4 py-2 text-left">Invoice #</th>
                                        <th className="px-4 py-2 text-left">Customer</th>
                                        <th className="px-4 py-2 text-left">Due Date</th>
                                        <th className="px-4 py-2 text-right">Days Overdue</th>
                                        <th className="px-4 py-2 text-right">Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(agingAR.buckets).flatMap(([bucket, docs]) => 
                                        docs.map(doc => (
                                            <tr key={doc.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                                <td className="px-4 py-2 font-medium text-blue-600">{doc.doc_no}</td>
                                                <td className="px-4 py-2">{doc.party}</td>
                                                <td className="px-4 py-2">{doc.due_date}</td>
                                                <td className="px-4 py-2 text-right">
                                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                                        doc.days_overdue <= 0 ? 'bg-green-100 text-green-700' :
                                                        doc.days_overdue <= 30 ? 'bg-blue-100 text-blue-700' :
                                                        doc.days_overdue <= 60 ? 'bg-amber-100 text-amber-700' :
                                                        'bg-red-100 text-red-700'
                                                    }`}>
                                                        {doc.days_overdue <= 0 ? 'Current' : `${doc.days_overdue} days`}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-right font-medium">{formatCurrency(doc.balance)}</td>
                                            </tr>
                                        ))
                                    )}
                                    {Object.values(agingAR.buckets).flat().length === 0 && (
                                        <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No outstanding invoices</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* AP Aging Tab */}
            {activeTab === 'aging-ap' && agingAP && (
                <div className="space-y-6">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-bold mb-4">{t('apAging')}</h2>
                        <p className="text-sm text-slate-500 mb-6">As of {agingAP.as_of_date}</p>
                        
                        {/* Aging Buckets Summary */}
                        <div className="grid grid-cols-6 gap-4 mb-8">
                            {[
                                { key: 'current', label: 'Current', color: 'bg-green-500' },
                                { key: '1_30', label: '1-30 Days', color: 'bg-blue-500' },
                                { key: '31_60', label: '31-60 Days', color: 'bg-amber-500' },
                                { key: '61_90', label: '61-90 Days', color: 'bg-orange-500' },
                                { key: 'over_90', label: '90+ Days', color: 'bg-red-500' },
                                { key: 'total', label: 'Total', color: 'bg-slate-700' },
                            ].map(bucket => (
                                <div key={bucket.key} className={`${bucket.color} rounded-lg p-4 text-white`}>
                                    <p className="text-xs opacity-80">{bucket.label}</p>
                                    <p className="text-xl font-bold">{formatCurrency(agingAP.totals[bucket.key])}</p>
                                </div>
                            ))}
                        </div>

                        {/* Aging Details */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="px-4 py-2 text-left">Bill #</th>
                                        <th className="px-4 py-2 text-left">Vendor</th>
                                        <th className="px-4 py-2 text-left">Due Date</th>
                                        <th className="px-4 py-2 text-right">Days Overdue</th>
                                        <th className="px-4 py-2 text-right">Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(agingAP.buckets).flatMap(([bucket, docs]) => 
                                        docs.map(doc => (
                                            <tr key={doc.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                                <td className="px-4 py-2 font-medium text-red-600">{doc.doc_no}</td>
                                                <td className="px-4 py-2">{doc.party}</td>
                                                <td className="px-4 py-2">{doc.due_date}</td>
                                                <td className="px-4 py-2 text-right">
                                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                                        doc.days_overdue <= 0 ? 'bg-green-100 text-green-700' :
                                                        doc.days_overdue <= 30 ? 'bg-blue-100 text-blue-700' :
                                                        doc.days_overdue <= 60 ? 'bg-amber-100 text-amber-700' :
                                                        'bg-red-100 text-red-700'
                                                    }`}>
                                                        {doc.days_overdue <= 0 ? 'Current' : `${doc.days_overdue} days`}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-right font-medium">{formatCurrency(doc.balance)}</td>
                                            </tr>
                                        ))
                                    )}
                                    {Object.values(agingAP.buckets).flat().length === 0 && (
                                        <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No outstanding bills</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Trial Balance Tab */}
            {activeTab === 'trial-balance' && trialBalance && (
                <div className="space-y-6">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-lg font-bold">{t('trialBalance')}</h2>
                                <p className="text-sm text-slate-500">As of {trialBalance.as_of_date}</p>
                            </div>
                            <div className={`px-4 py-2 rounded-lg ${trialBalance.is_balanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {trialBalance.is_balanced ? t('balanced') : t('unbalanced')}
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-100 dark:bg-slate-700">
                                        <th className="px-4 py-3 text-left font-semibold">Account Code</th>
                                        <th className="px-4 py-3 text-left font-semibold">Account Name</th>
                                        <th className="px-4 py-3 text-center font-semibold">Type</th>
                                        <th className="px-4 py-3 text-right font-semibold">Debit</th>
                                        <th className="px-4 py-3 text-right font-semibold">Credit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {trialBalance.accounts.filter(a => a.debit > 0 || a.credit > 0).map((account, index) => (
                                        <tr key={index} className="border-b hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                            <td className="px-4 py-2 font-mono">{account.account_code}</td>
                                            <td className="px-4 py-2">{account.account_name}</td>
                                            <td className="px-4 py-2 text-center">
                                                <span className={`px-2 py-0.5 rounded text-xs capitalize ${
                                                    account.account_type === 'asset' ? 'bg-blue-100 text-blue-700' :
                                                    account.account_type === 'liability' ? 'bg-red-100 text-red-700' :
                                                    account.account_type === 'equity' ? 'bg-purple-100 text-purple-700' :
                                                    account.account_type === 'income' ? 'bg-green-100 text-green-700' :
                                                    'bg-orange-100 text-orange-700'
                                                }`}>
                                                    {account.account_type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-right font-medium">{account.debit > 0 ? formatCurrency(account.debit) : '-'}</td>
                                            <td className="px-4 py-2 text-right font-medium">{account.credit > 0 ? formatCurrency(account.credit) : '-'}</td>
                                        </tr>
                                    ))}
                                    {trialBalance.accounts.filter(a => a.debit > 0 || a.credit > 0).length === 0 && (
                                        <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No account balances</td></tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-slate-200 dark:bg-slate-700 font-bold">
                                        <td colSpan={3} className="px-4 py-3">TOTALS</td>
                                        <td className="px-4 py-3 text-right">{formatCurrency(trialBalance.total_debit)}</td>
                                        <td className="px-4 py-3 text-right">{formatCurrency(trialBalance.total_credit)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reports;
